import React, { useState } from 'react';

function Cidr() {
  // IP & Netmask Calculator state
  const [ipAddress, setIpAddress] = useState('');
  const [netmask, setNetmask] = useState('');
  const [gateway, setGateway] = useState('');
  const [ipNetmaskResults, setIpNetmaskResults] = useState(null);
  const [showIpNetmaskHosts, setShowIpNetmaskHosts] = useState(false);

  // CIDR Calculator state
  const [cidrInput, setCidrInput] = useState('');
  const [cidrResults, setCidrResults] = useState(null);
  const [showCidrHosts, setShowCidrHosts] = useState(false);

  // Copy to clipboard state
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = (text, fieldName) => {
    if (text) {
      navigator.clipboard.writeText(text).then(() => {
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(null), 2000);
      });
    }
  };

  const validateCIDR = input => {
    const cidrRegex =
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/(3[0-2]|[12]?[0-9])$/;
    return cidrRegex.test(input);
  };

  const ipToDecimal = ip => {
    return ip
      .split('.')
      .map(Number)
      .reduce((acc, part) => acc * 256 + part, 0);
  };

  const decimalToIp = decimal => {
    return [
      (decimal >>> 24) & 255,
      (decimal >>> 16) & 255,
      (decimal >>> 8) & 255,
      decimal & 255,
    ].join('.');
  };

  const calculateNetmask = prefix => {
    const maskBinary = new Array(32).fill(0).fill(1, 0, prefix).join('');
    return [
      parseInt(maskBinary.slice(0, 8), 2),
      parseInt(maskBinary.slice(8, 16), 2),
      parseInt(maskBinary.slice(16, 24), 2),
      parseInt(maskBinary.slice(24, 32), 2),
    ].join('.');
  };

  const validateIP = ip => {
    const ipRegex =
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  const netmaskToCIDR = netmask => {
    const parts = netmask.split('.').map(Number);
    const binary = parts
      .map(part => part.toString(2).padStart(8, '0'))
      .join('');
    return binary.indexOf('0') === -1 ? 32 : binary.indexOf('0');
  };

  const calculateFromIPNetmask = () => {
    if (!validateIP(ipAddress)) {
      alert('Invalid IP address format.');
      return;
    }
    if (!validateIP(netmask)) {
      alert('Invalid netmask format.');
      return;
    }
    if (gateway && !validateIP(gateway)) {
      alert('Invalid gateway format.');
      return;
    }

    const prefix = netmaskToCIDR(netmask);
    const totalHosts = Math.pow(2, 32 - prefix) - 2;

    const networkAddress = ipToDecimal(ipAddress) & (-1 << (32 - prefix));
    const broadcastAddress = networkAddress + Math.pow(2, 32 - prefix) - 1;
    const firstUsableIP = networkAddress + 1;

    let usableHosts = [];
    for (let i = 1; i <= totalHosts; i++) {
      const nextIPDecimal = networkAddress + i;
      if (nextIPDecimal >= broadcastAddress) break;
      usableHosts.push(decimalToIp(nextIPDecimal));
    }

    setIpNetmaskResults({
      totalHosts,
      subnetID: decimalToIp(networkAddress),
      broadcast: decimalToIp(broadcastAddress),
      netmask,
      gateway: gateway || decimalToIp(firstUsableIP),
      usableHosts,
      cidrNotation: `${ipAddress}/${prefix}`,
      hasCustomGateway: !!gateway,
    });
  };

  const calculateHostsFromCIDR = () => {
    if (!validateCIDR(cidrInput)) {
      alert(
        "Invalid CIDR format. Please enter in the format 'xxx.xxx.xxx.xxx/xx'."
      );
      return;
    }

    const [ip, prefixLength] = cidrInput.split('/');
    const prefix = parseInt(prefixLength);
    const totalHosts = Math.pow(2, 32 - prefix) - 2;

    const networkAddress = ipToDecimal(ip) & (-1 << (32 - prefix));
    const broadcastAddress = networkAddress + Math.pow(2, 32 - prefix) - 1;
    const firstUsableIP = networkAddress + 1;
    const netmask = calculateNetmask(prefix);

    let usableHosts = [];
    for (let i = 1; i <= totalHosts; i++) {
      const nextIPDecimal = networkAddress + i;
      if (nextIPDecimal >= broadcastAddress) break;
      usableHosts.push(decimalToIp(nextIPDecimal));
    }

    setCidrResults({
      totalHosts,
      subnetID: decimalToIp(networkAddress),
      broadcast: decimalToIp(broadcastAddress),
      netmask,
      gateway: decimalToIp(firstUsableIP),
      usableHosts,
    });
  };

  return (
    <div className="h-full bg-gray-100 p-8 flex flex-col items-center overflow-y-auto">
      <div className="w-full max-w-7xl flex flex-col md:flex-row gap-8">
        {/* IP & Netmask Calculator */}
        <div className="bg-white shadow-lg rounded-lg p-8 flex-1">
          <h1 className="text-3xl font-bold mb-6 text-gray-900">
            Enter IP & Netmask
          </h1>

          <div className="space-y-4 mb-6">
            <div>
              <label
                htmlFor="ipAddress"
                className="block text-gray-600 text-sm mb-2"
              >
                Enter IP Address
              </label>
              <div className="relative">
                <input
                  id="ipAddress"
                  type="text"
                  value={ipAddress}
                  onChange={e => setIpAddress(e.target.value)}
                  placeholder="12.132.208.106"
                  className="w-full border border-gray-300 rounded-md px-4 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => copyToClipboard(ipAddress, 'ipAddress')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                  title="Copy to clipboard"
                >
                  <img
                    src="/img/svg-copy-to-clipboard.svg"
                    alt="Copy"
                    className="w-5 h-5"
                  />
                  {copiedField === 'ipAddress' && (
                    <span className="absolute -top-8 right-0 bg-green-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                      Copied!
                    </span>
                  )}
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor="netmask"
                className="block text-gray-600 text-sm mb-2"
              >
                Enter Netmask
              </label>
              <div className="relative">
                <input
                  id="netmask"
                  type="text"
                  value={netmask}
                  onChange={e => setNetmask(e.target.value)}
                  placeholder="255.255.255.248"
                  className="w-full border border-gray-300 rounded-md px-4 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => copyToClipboard(netmask, 'netmask')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                  title="Copy to clipboard"
                >
                  <img
                    src="/img/svg-copy-to-clipboard.svg"
                    alt="Copy"
                    className="w-5 h-5"
                  />
                  {copiedField === 'netmask' && (
                    <span className="absolute -top-8 right-0 bg-green-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                      Copied!
                    </span>
                  )}
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor="gateway"
                className="block text-gray-600 text-sm mb-2"
              >
                Default Gateway (Optional)
              </label>
              <div className="relative">
                <input
                  id="gateway"
                  type="text"
                  value={gateway}
                  onChange={e => setGateway(e.target.value)}
                  placeholder="12.132.208.105"
                  className="w-full border border-gray-300 rounded-md px-4 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => copyToClipboard(gateway, 'gateway')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                  title="Copy to clipboard"
                >
                  <img
                    src="/img/svg-copy-to-clipboard.svg"
                    alt="Copy"
                    className="w-5 h-5"
                  />
                  {copiedField === 'gateway' && (
                    <span className="absolute -top-8 right-0 bg-green-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                      Copied!
                    </span>
                  )}
                </button>
              </div>
            </div>

            <button
              onClick={calculateFromIPNetmask}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded shadow-md transition-colors"
            >
              Calculate Usable Hosts
            </button>
          </div>

          {ipNetmaskResults && (
            <>
              <div className="bg-gray-50 p-4 rounded-md space-y-2 mb-4">
                <p>
                  <strong>CIDR Notation:</strong> {ipNetmaskResults.cidrNotation}
                </p>
                <p>
                  <strong>Total Hosts:</strong> {ipNetmaskResults.totalHosts}
                </p>
                <p>
                  <strong>Subnet ID:</strong> {ipNetmaskResults.subnetID}
                </p>
                <p>
                  <strong>Broadcast:</strong> {ipNetmaskResults.broadcast}
                </p>
                <p>
                  <strong>Netmask:</strong> {ipNetmaskResults.netmask}
                </p>
                <p>
                  <strong>Default Gateway:</strong>{' '}
                  {ipNetmaskResults.hasCustomGateway
                    ? ipNetmaskResults.gateway
                    : 'To be determined'}
                </p>
              </div>

              <div className="text-center mb-4">
                <button
                  onClick={() => setShowIpNetmaskHosts(!showIpNetmaskHosts)}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded shadow-md transition-colors"
                >
                  Show/Hide
                </button>
              </div>

              {showIpNetmaskHosts && (
                <>
                  <ul className="max-h-64 overflow-auto bg-white border border-gray-200 space-y-1 text-sm p-4 rounded-md mb-2">
                    {ipNetmaskResults.usableHosts.map((ip, idx) => (
                      <li key={idx} className="text-gray-700">
                        {ip}
                        {ipNetmaskResults.hasCustomGateway &&
                          ip === ipNetmaskResults.gateway &&
                          ' *'}
                        {!ipNetmaskResults.hasCustomGateway &&
                          idx === 0 && (
                            <span className="text-red-600 ml-2 font-semibold">
                              suggested/commonly used as Default Gateway
                            </span>
                          )}
                      </li>
                    ))}
                  </ul>
                  {ipNetmaskResults.hasCustomGateway && (
                    <p className="text-sm text-gray-600 italic">
                      Note: * Default Gateway
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* CIDR Subnet Calculator */}
        <div className="bg-white shadow-lg rounded-lg p-8 flex-1">
          <h1 className="text-3xl font-bold mb-6 text-gray-900">
            CIDR Subnet Calculator
          </h1>

          <div className="space-y-4 mb-6">
            <div>
              <label
                htmlFor="cidrInput"
                className="block text-gray-600 text-sm mb-2"
              >
                Enter Subnet in CIDR notation
              </label>
              <div className="relative">
                <input
                  id="cidrInput"
                  type="text"
                  value={cidrInput}
                  onChange={e => setCidrInput(e.target.value)}
                  placeholder="12.132.208.106/29"
                  className="w-full border border-gray-300 rounded-md px-4 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => copyToClipboard(cidrInput, 'cidrInput')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                  title="Copy to clipboard"
                >
                  <img
                    src="/img/svg-copy-to-clipboard.svg"
                    alt="Copy"
                    className="w-5 h-5"
                  />
                  {copiedField === 'cidrInput' && (
                    <span className="absolute -top-8 right-0 bg-green-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                      Copied!
                    </span>
                  )}
                </button>
              </div>
            </div>

            <button
              onClick={calculateHostsFromCIDR}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded shadow-md transition-colors"
            >
              Calculate Usable Hosts
            </button>
          </div>

          {cidrResults && (
            <>
              <div className="bg-gray-50 p-4 rounded-md space-y-2 mb-4">
                <p>
                  The Subnet ID (Network Address) is: {cidrResults.subnetID}
                </p>
                <p>
                  The Netmask in octal notation is: {cidrResults.netmask}
                </p>
                <p>
                  The suggested default gateway is: {cidrResults.gateway}{' '}
                  (typically the first usable IP in the subnet).
                </p>
                <p>The Broadcast Address is: {cidrResults.broadcast}</p>
              </div>

              <div className="text-center mb-4">
                <button
                  onClick={() => setShowCidrHosts(!showCidrHosts)}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded shadow-md transition-colors"
                >
                  Show/Hide
                </button>
              </div>

              {showCidrHosts && (
                <ul className="max-h-64 overflow-auto bg-white border border-gray-200 space-y-1 text-sm p-4 rounded-md">
                  {cidrResults.usableHosts.map((ip, idx) => (
                    <li key={idx} className="text-gray-700">
                      {ip}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Cidr;
